/**
 * `/stories/new` — the character sheet.
 *
 * The route's own job is the two questions the form must not have to ask: **is there a slot
 * free** (P7 — checked here so the form is never rendered into a guaranteed 403) and **what
 * language does this player read** (the profile's locale, which is the API's own default for
 * a story that does not name one).
 */
import { Link } from 'react-router';
import { describeApiError } from '@/api/errorMessages';
import { usePlayGate, useProfile, useStoryBudget } from '@/api/queries';
import { CharacterCreation } from '@/features/stories/CharacterCreation';
import { useT } from '@/i18n/context';
import { Page } from '@/ui/Page';

export function NewStoryRoute() {
  const t = useT();
  const profile = useProfile();
  const budget = useStoryBudget();
  const gate = usePlayGate();

  return (
    <Page
      title={t('ui.character.create')}
      back={{ to: '/stories', label: t('ui.nav.your_stories') }}
    >
      {profile.isPending ? (
        <p className="text-fg-muted" style={{ fontSize: 'var(--text-action)' }}>
          {t('ui.common.loading')}
        </p>
      ) : profile.error ? (
        <p className="text-fg-muted" style={{ fontSize: 'var(--text-action)' }}>
          {describeApiError(t, profile.error)}
        </p>
      ) : budget?.canCreate === false ? (
        <div className="flex flex-col gap-4">
          <p className="text-fg-muted" style={{ fontSize: 'var(--text-action)' }}>
            {/* Same precedence as the shelf: a play gate is not a slot problem, and naming
             * the wrong one sends the player off to delete a story that would not help. */}
            {gate?.canPlay === false
              ? t(gate.reason ?? 'ui.error.forbidden')
              : t('ui.story.slots_full', { max: budget.max })}
          </p>
          <Link
            to="/stories"
            className="border-border text-fg-muted hover:border-accent hover:text-accent w-fit border px-4 py-2 font-mono transition-colors"
            style={{ fontSize: 'var(--text-action)' }}
          >
            {t('ui.nav.your_stories')}
          </Link>
        </div>
      ) : (
        <>
          <p className="text-fg-faint" style={{ fontSize: 'var(--text-meta)', lineHeight: 1.6 }}>
            {t('ui.character.intro')}
          </p>
          <CharacterCreation defaultLocale={profile.data.locale} />
        </>
      )}
    </Page>
  );
}
